import isEqual from 'lodash/isEqual'

import type { CommentAnchor, CommentThread } from './commentTypes'

function cleanAnchor(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  )
}

/**
 * Deep-equal comparison of two anchors. Filters out `undefined` values
 * so that omitted optional fields (e.g. flowchart `nodeId`) don't cause
 * false negatives.
 */
export function matchAnchors(a: CommentAnchor, b: CommentAnchor): boolean {
  return isEqual(cleanAnchor(a), cleanAnchor(b))
}

export type AnchorIndex = Map<string, CommentThread[]>

/**
 * Build an index keyed by `stepId` for fast lookup. Every anchor type
 * includes `stepId`, so this gives O(1) access to a step's threads.
 */
export function buildAnchorIndex(
  threads: ReadonlyArray<CommentThread>
): AnchorIndex {
  const index: AnchorIndex = new Map()
  for (const thread of threads) {
    const key = thread.anchor.stepId
    const list = index.get(key)
    if (list) {
      list.push(thread)
    } else {
      index.set(key, [thread])
    }
  }
  return index
}

/**
 * Return threads matching the given anchor, scoped to the anchor's step
 * via the pre-built index for O(threads-in-step) instead of O(all-threads).
 */
export function getThreadsForAnchorFromIndex(
  index: AnchorIndex,
  anchor: CommentAnchor
): CommentThread[] {
  const stepThreads = index.get(anchor.stepId) ?? []
  return stepThreads.filter((t) => matchAnchors(t.anchor, anchor))
}

export function anchorBelongsToZone(
  anchor: CommentAnchor,
  stepId: string,
  zoneId: string
): boolean {
  if (anchor.stepId !== stepId) return false
  switch (anchor.type) {
    case 'zone':
    case 'zone-property':
    case 'damage-mechanism':
      return anchor.zoneId === zoneId
    default:
      return false
  }
}
